import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon } from 'lucide-react';
import { objectionChoices, objectionModal, ObjectionId } from '../data/objections';
import { discountOffer } from '../data/product';
import { trackCustom } from '../utils/tracking';
interface ObjectionModalProps {
  open: boolean;
  onClose: () => void;
  onChoose: (id: ObjectionId) => void;
  /** "None of these, I want it": take the code and go straight to the packs. */
  onWantIt: () => void;
}

/**
 * Centered modal on desktop, bottom sheet on mobile. Asks what is holding the
 * visitor back, then reveals the matching personalized funnel further down.
 */
export function ObjectionModal({
  open,
  onClose,
  onChoose,
  onWantIt
}: ObjectionModalProps) {
  useEffect(() => {
    if (!open) return;
    trackCustom('objection_popup_viewed');
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const dismiss = () => {
    trackCustom('objection_popup_closed');
    onClose();
  };
  return <AnimatePresence data-id="element-510" data-mp="pzoy6liec55i">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.16,
      ease: [0.23, 1, 0.32, 1]
    }} role="dialog" aria-modal="true" aria-labelledby="objection-heading" data-id="element-511" data-mp="pynl6bhtl8l5">
          <motion.div className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto border-t-2 border-grit-500 bg-ink-950 sm:border-2" initial={{
        opacity: 0,
        y: 28,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 18,
        scale: 0.98
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-512" data-mp="72nvx8g88j59">
            <button type="button" onClick={dismiss} aria-label="Close" className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center text-steel-400 transition-colors duration-150 ease-grit hover:text-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-513" data-mp="3eb1gan97dnfm">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-514" data-mp="x6j1u4x4oq9" />
            </button>

            <div className="p-5 sm:p-7" data-id="element-515" data-mp="3t1s65pr8icg3">
              <h2 id="objection-heading" className="grit-headline pr-10 text-[clamp(1.75rem,6vw,2.75rem)] font-extrabold uppercase leading-[0.95] text-offwhite" data-id="element-516" data-mp="3t2ttasliype6">
                {objectionModal.headline}
              </h2>
              <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-517" data-mp="kna3k3k2bp3v">
                {objectionModal.subheadline}
              </p>
              <p className="mt-2 text-[14px] leading-relaxed text-steel-400" data-id="element-518" data-mp="n6n1jchjku3c">{objectionModal.support}</p>

              <ul className="mt-5 grid gap-2" data-id="element-519" data-mp="s5hq6v26xcvd">
                {objectionChoices.map(choice => <li key={choice.id} data-id="element-520" data-mp="x52ttisb29ef">
                    <button type="button" onClick={() => onChoose(choice.id)} className="group flex w-full items-center justify-between gap-3 border border-white/15 bg-ink-900 px-4 py-3.5 text-left transition-[background-color,border-color] duration-150 ease-grit hover:border-grit-500 hover:bg-ink-850 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-521" data-mp="x41bct8ymkmo">
                      <span data-id="element-522" data-mp="1kux1zn1oj9sc">
                        <span className="block font-display text-lg font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-523" data-mp="1kt3uk5j8slja">
                          {choice.label}
                        </span>
                        {choice.sub && <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-524" data-mp="1g8d0idzq06hj">{choice.sub}</span>}
                      </span>
                      <ArrowRightIcon className="h-5 w-5 shrink-0 text-grit-500 transition-transform duration-150 ease-grit group-hover:translate-x-1" aria-hidden="true" data-id="element-525" data-mp="1c5zvxswpmemc" />
                    </button>
                  </li>)}
              </ul>

              <button type="button" data-buy-intent="true" onClick={onWantIt} className="mt-4 flex w-full items-center justify-between gap-3 border-2 border-grit-500 bg-grit-500 px-4 py-3.5 text-left transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-526" data-mp="xblallpoat4i">
                <span data-id="element-527" data-mp="2dmp92ydgpbyk">
                  <span className="block font-display text-lg font-bold uppercase leading-tight tracking-wide text-ink-950" data-id="element-528" data-mp="2dn8u4nyesggt">
                    {objectionModal.wantIt.label}
                  </span>
                  <span className="mt-0.5 block text-[13px] font-semibold text-ink-950/75" data-id="element-529" data-mp="4gdow0rf03r2">
                    {objectionModal.wantIt.sub}
                  </span>
                </span>
                <span className="shrink-0 text-right" data-id="element-530" data-mp="3rkl90sqzl7pq">
                  <span className="block bg-ink-950 px-2.5 py-1 font-display text-lg font-extrabold uppercase leading-none text-grit-500" data-id="element-531" data-mp="j48jjmozgi6s">
                    {objectionModal.wantIt.badge}
                  </span>
                  <span className="mt-1 block text-[11px] font-bold uppercase tracking-[0.18em] text-ink-950" data-id="element-532" data-mp="9eyqa3iekth3">
                    Code {discountOffer.code}
                  </span>
                </span>
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}